import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Play, Save, Loader2, CheckCircle, AlertCircle, Video } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Define all sections with their display names and groupings
const HELP_SECTIONS = [
  { group: 'CRM', items: [
    { key: 'member-tracker', label: 'Member Tracker' },
    { key: 'companies', label: 'Companies' },
    { key: 'contacts', label: 'Contacts' },
    { key: 'tasks', label: 'Tasks' },
    { key: 'proposals', label: 'Proposals' },
    { key: 'contracts', label: 'Contracts & Payments' },
  ]},
  { group: 'Analytics', items: [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'intelligence', label: 'Intelligence' },
    { key: 'goals', label: 'Goals' },
  ]},
  { group: 'Operations', items: [
    { key: 'events', label: 'Events Calendar' },
    { key: 'media-production', label: 'Media Production' },
  ]},
  { group: 'Support', items: [
    { key: 'documentation', label: 'Documentation' },
    { key: 'bugs-requests', label: 'Bugs & Requests' },
    { key: 'admin', label: 'Admin' },
  ]},
];

const HelpFilesAdmin = () => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [videos, setVideos] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchVideos();
  }, [user, navigate]);

  const fetchVideos = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/help-videos`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Convert array to object keyed by section
        const videoMap = {};
        data.forEach(v => {
          videoMap[v.section] = v.video_url || '';
        });
        setVideos(videoMap);
      }
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUrlChange = (section, url) => {
    setVideos(prev => ({ ...prev, [section]: url }));
  };

  const saveVideo = async (section) => {
    setSaving(section);
    setMessage(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/help-videos/${section}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ video_url: videos[section] || '' })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: `Video URL saved for ${section}` });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to save' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save video URL' });
    } finally {
      setSaving(null);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const saveAll = async () => {
    setSaving('all');
    setMessage(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/help-videos/bulk`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ videos })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'All video URLs saved successfully' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to save' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save video URLs' });
    } finally {
      setSaving(null);
    }
  };

  // Extract YouTube video ID for preview thumbnail
  const getYouTubeThumbnail = (url) => {
    if (!url) return null;
    const watchMatch = url.match(/[?&]v=([^&]+)/);
    if (watchMatch) return `https://img.youtube.com/vi/${watchMatch[1]}/default.jpg`;
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) return `https://img.youtube.com/vi/${shortMatch[1]}/default.jpg`;
    return null;
  };

  if (user?.role !== 'admin') return null;

  return (
    <AppLayout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className={`flex items-center gap-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} mb-6 transition-colors`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Video className="w-8 h-8 text-[#A2FF00]" />
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Help Video Management
              </h1>
              <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Configure YouTube video URLs for each section
              </p>
            </div>
          </div>
          <Button
            onClick={saveAll}
            disabled={saving === 'all'}
            className="bg-[#A2FF00] text-black hover:bg-[#8FE600]"
          >
            {saving === 'all' ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving All...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Save All</>
            )}
          </Button>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${message.type === 'success' ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-400" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-400" />
            )}
            <span className={message.type === 'success' ? 'text-green-400' : 'text-red-400'}>
              {message.text}
            </span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#A2FF00]" />
          </div>
        ) : (
          <div className="space-y-8">
            {HELP_SECTIONS.map((group) => (
              <div key={group.group} className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg overflow-hidden`}>
                <div className={`px-4 py-3 ${isDark ? 'bg-[#0A0A0A] border-b border-white/10' : 'bg-gray-50 border-b border-gray-200'}`}>
                  <h2 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{group.group}</h2>
                </div>
                <div className="divide-y divide-white/5">
                  {group.items.map((item) => (
                    <div key={item.key} className="p-4">
                      <div className="flex items-center gap-4">
                        {/* Thumbnail preview */}
                        <div className={`w-16 h-12 rounded overflow-hidden flex-shrink-0 ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                          {getYouTubeThumbnail(videos[item.key]) ? (
                            <img 
                              src={getYouTubeThumbnail(videos[item.key])} 
                              alt="" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Play className={`w-5 h-5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                            </div>
                          )}
                        </div>
                        
                        {/* Label */}
                        <div className="w-40 flex-shrink-0">
                          <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {item.label}
                          </span>
                        </div>
                        
                        {/* URL Input */}
                        <div className="flex-1">
                          <Input
                            value={videos[item.key] || ''}
                            onChange={(e) => handleUrlChange(item.key, e.target.value)}
                            placeholder="https://www.youtube.com/watch?v=..."
                            className={`${isDark ? 'bg-[#0A0A0A] border-white/20' : ''}`}
                          />
                        </div>
                        
                        {/* Save Button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => saveVideo(item.key)}
                          disabled={saving === item.key}
                        >
                          {saving === item.key ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default HelpFilesAdmin;
